import * as Sentry from '@sentry/react';

const API_BASE = import.meta.env.VITE_API_URL || '';

let portalToken: string | null = null;

export function setPortalToken(token: string) {
  portalToken = token;
}

export function getPortalToken(): string | null {
  return portalToken;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}/api/portal${path}`, {
    ...options,
    headers: {
      ...(portalToken ? { 'X-Portal-Token': portalToken } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401) {
    portalToken = null;
    localStorage.removeItem('portal-token');
    window.location.href = '/invalid';
    throw new Error('Token expired');
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Unknown error' }));
    const err = new Error(error.error || `HTTP ${res.status}`);
    if (res.status >= 500) {
      Sentry.captureException(err, {
        extra: { status: res.status, method: options.method ?? 'GET', path },
      });
    }
    throw err;
  }

  return res.json();
}

export const portalApi = {
  getContext: () => request<any>('/context'),
  uploadDocument: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return request<{ id: string }>('/upload', {
      method: 'POST',
      body: formData,
      // Don't set Content-Type -- browser sets it with boundary for FormData
    });
  },
  getDocuments: () => request<any>('/documents'),
  getDeadlines: () => request<any>('/deadlines'),
  getRequests: () => request<any>('/requests'),
  getVapidKey: () => request<{ publicKey: string }>('/vapid-key'),
  pushSubscribe: (subscription: PushSubscriptionJSON) =>
    request<{ ok: boolean }>('/push-subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscription),
    }),
  getThreads: () => request<any>('/threads'),
  getThread: (id: string) => request<any>(`/threads/${id}`),
  sendMessage: (threadId: string, body: string) =>
    request<any>(`/threads/${threadId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    }),
  markThreadRead: (threadId: string) =>
    request<any>(`/threads/${threadId}/read`, { method: 'POST' }),
  getUnreadCount: () => request<{ unreadCount: number }>('/unread-count'),
};
